import type React from 'react';
import { FolderOpen, GitBranch, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  withTruncation,
  type TruncationInjectedProps,
} from '@/components/shared/hocs/withTruncation';
import { MiddleTruncatedPath } from '@/components/shared/MiddleTruncatedPath';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { PlatformBadge } from '@/components/shared/platform';
import { cn } from '@/lib/utils';
import type { HookInventoryRow } from '@kirin/api-client/contracts/hooks';
import { getVirtualPathDetails } from '@kirin/api-client/contracts';
import { FAMILY_BADGE_CLASSES } from './constants';
import { getHookFileDisplayLabel, normalizePathForDisplay } from './fileLabel';

interface CellProps {
  row: HookInventoryRow;
}

interface ProjectsCellProps extends CellProps {
  onProjectsClick: (groupKey: string) => void;
}

interface UsersCellProps extends CellProps {
  onUsersClick: (groupKey: string) => void;
}

// ── Git badge (shared) ────────────────────────────────────
// Single source for the Git chip so the table cell, the detail modal, and the
// IDE/Platform filter dropdown all render the SAME git-branch glyph (KIR-3643).
// Git is not a PLATFORM_REGISTRY platform, so PlatformBadge would fall back to
// the `</>` Code2 icon — this keeps every surface on the branch icon.
export function GitBadge(): React.ReactElement {
  return (
    <Badge variant="outline" className="gap-1">
      <GitBranch className="h-3 w-3 shrink-0" />
      Git
    </Badge>
  );
}

// ── Type + event ──────────────────────────────────────────
// Colored highlight lives here (mirrors AI Instructions' Type column):
// the pill is tinted by source family. The whole row is clickable
// (onRowClick on the table) so the badge stays non-interactive.
interface TruncatedTypeProps extends TruncationInjectedProps {
  badgeClass: string;
  label: string;
}

const TruncatedType = withTruncation<TruncatedTypeProps>(
  ({ badgeClass, label, truncationRef, isTruncated, checkTruncation }) => {
    const badge = (
      <Badge
        ref={truncationRef}
        variant="outline"
        className={cn(
          badgeClass,
          'max-w-full inline-block truncate align-middle'
        )}
        onMouseEnter={checkTruncation}
      >
        {label}
      </Badge>
    );

    if (!isTruncated) {
      return badge;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-md whitespace-pre-wrap">
          {label}
        </TooltipContent>
      </Tooltip>
    );
  }
);

export function TypeCell({ row }: CellProps): React.ReactElement {
  const family = row.sourceFamily ?? '';
  const badgeClass =
    FAMILY_BADGE_CLASSES[family] ?? 'bg-muted text-muted-foreground';
  return (
    <TruncatedType badgeClass={badgeClass} label={row.fileCategory ?? '—'} />
  );
}

// ── Platform / IDE chip ───────────────────────────────────
// Same chip the other inventory tables use (PlatformBadge: branded icon
// + registry label, neutral outline). Git hooks aren't a registry
// platform, so they get an explicit git-branch chip styled to match.
export function PlatformCell({ row }: CellProps): React.ReactElement {
  // Git-family hooks aren't a registry platform and legitimately carry
  // `idePlatform: null`, so the Git chip must be decided before the
  // null-platform fallback — otherwise git hooks render "—".
  if (row.sourceFamily === 'git') {
    return <GitBadge />;
  }

  const platform = row.idePlatform;
  if (!platform) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }

  return <PlatformBadge platform={platform} />;
}

// ── File ──────────────────────────────────────────────────
// Virtual rows (e.g. `vscode-settings://kirin.hooks`, hooks declared in an IDE
// settings.json block) aren't real filesystem paths: they render a friendly
// label and expose the raw URI + backing file via tooltip. Mirrors the AI
// Instructions tab (KIR-2595 / KIR-2917).
type VirtualFileProps = CellProps & TruncationInjectedProps;

const VirtualFile = withTruncation<VirtualFileProps>(
  ({ row, truncationRef, checkTruncation }) => {
    const virtualDetails = getVirtualPathDetails(row.filePath);
    // VirtualFile is only mounted for virtual rows (see FileCell guard), so
    // `virtualDetails` is always present here.
    if (!virtualDetails) {
      return null;
    }
    const content = (
      <span
        ref={truncationRef}
        className="font-mono text-xs text-muted-foreground truncate block max-w-full"
        onMouseEnter={checkTruncation}
      >
        {getHookFileDisplayLabel(row.filePath)}
      </span>
    );

    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-md whitespace-pre-wrap">
          {`Source: ${virtualDetails.source}\nBacked by: ${virtualDetails.backedBy}`}
        </TooltipContent>
      </Tooltip>
    );
  }
);

export function FileCell({ row }: CellProps): React.ReactElement {
  if (getVirtualPathDetails(row.filePath)) {
    return <VirtualFile row={row} />;
  }
  // KIR-2563: real paths use width-measured middle truncation. When a row spans
  // multiple paths, surface them all (copyable) via the popover body.
  const displayFilePaths = (row.filePaths ?? []).map(normalizePathForDisplay);
  const copyText =
    displayFilePaths.length > 1 ? displayFilePaths.join('\n') : undefined;
  return (
    <MiddleTruncatedPath
      path={getHookFileDisplayLabel(row.filePath)}
      copyText={copyText}
      className="text-xs text-muted-foreground"
    />
  );
}

// ── Hook details (summary) ────────────────────────────────
type TruncatedSummaryProps = CellProps & TruncationInjectedProps;

const TruncatedSummary = withTruncation<TruncatedSummaryProps>(
  ({ row, truncationRef, isTruncated, checkTruncation }) => {
    if (!row.summary) {
      return <span className="text-muted-foreground text-sm">—</span>;
    }

    const content = (
      <span
        ref={truncationRef}
        className="text-sm truncate block max-w-full"
        onMouseEnter={checkTruncation}
      >
        {row.summary}
      </span>
    );

    if (!isTruncated) {
      return content;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-md whitespace-pre-wrap">
          {row.summary}
        </TooltipContent>
      </Tooltip>
    );
  }
);

export function SummaryCell({ row }: CellProps): React.ReactElement {
  return <TruncatedSummary row={row} />;
}

// ── Projects ──────────────────────────────────────────────
export function ProjectsCell({
  row,
  onProjectsClick,
}: ProjectsCellProps): React.ReactElement {
  const count = row.projects?.length ?? 0;
  if (count === 0) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }
  return (
    <Button
      variant="link"
      size="sm"
      className="h-auto p-0 gap-1.5 underline decoration-primary/40 underline-offset-2 hover:decoration-primary transition-colors"
      onClick={e => {
        e.stopPropagation();
        onProjectsClick(row.groupKey);
      }}
      title={`${count} project${count !== 1 ? 's' : ''} with this hook`}
    >
      <FolderOpen className="h-3.5 w-3.5" />
      {count}
    </Button>
  );
}

// ── Users (KIR-3643) ──────────────────────────────────────
// Count of unique users the hook is installed for; clicking opens the users
// list (mirrors the Projects cell). The row carries the full users array, so
// the count is `row.users.length`.
export function UsersCell({
  row,
  onUsersClick,
}: UsersCellProps): React.ReactElement {
  const count = row.users?.length ?? 0;
  if (count === 0) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }
  return (
    <Button
      variant="link"
      size="sm"
      className="h-auto p-0 gap-1.5 underline decoration-primary/40 underline-offset-2 hover:decoration-primary transition-colors"
      onClick={e => {
        e.stopPropagation();
        onUsersClick(row.groupKey);
      }}
      title={`${count} user${count !== 1 ? 's' : ''} with this hook`}
    >
      <Users className="h-3.5 w-3.5" />
      {count}
    </Button>
  );
}

// ── Last Seen (KIR-3643) ──────────────────────────────────
// Timestamp of the most recent detection of the hook (relative, with an
// absolute-time tooltip).
export function LastSeenCell({ row }: CellProps): React.ReactElement {
  if (!row.lastSeenAt) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <span className="text-muted-foreground">
      <RelativeTimeWithTooltip date={row.lastSeenAt} />
    </span>
  );
}
